// components/admin/FamilyForm.js - Versione refactored e pulita
import React, { useState, useEffect, useCallback } from "react"
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd"
import {
  fetchMaterials,
  fetchTechnicalTypes,
  fetchProductByCode,
} from "../../api"
import { materialManager } from "../../materialManager"
import TypeLinksComponent from "./TypeLinksComponent"
import MaterialTypeManager from "./MaterialTypeManager"
import {
  FiEye,
  FiEyeOff,
  FiCheck,
  FiX,
  FiAlertCircle,
  FiMove,
  FiEdit,
  FiTrash2,
  FiPlus,
} from "react-icons/fi"

const FamilyForm = ({
  category,
  family,
  isEditing,
  initialData,
  onSave,
  onCancel,
  onProductChange,
  currentSelectedProduct,
  products,
}) => {
  // === CORE STATE ===
  const [formData, setFormData] = useState({
    name: "",
    defaultMaterials: {},
    typeLinks: [],
    collectionTranslations: {},
  })

  // === MATERIALS STATE ===
  const [allMaterials, setAllMaterials] = useState([])
  const [collections, setCollections] = useState([])
  const [customTypes, setCustomTypes] = useState([])
  const [typeLinks, setTypeLinks] = useState([])

  // === PRODUCT CROSS-CHECK STATE ===
  const [selectedProduct, setSelectedProduct] = useState(null)
  const [fetchedMaterials, setFetchedMaterials] = useState([])
  const [availableFamilyProducts, setAvailableFamilyProducts] = useState([])
  const [crossCheckEnabled, setCrossCheckEnabled] = useState(false)
  const [materialComparisonData, setMaterialComparisonData] = useState({})
  const [isLoadingProduct, setIsLoadingProduct] = useState(false)
  const [crossCheckKey, setCrossCheckKey] = useState(0)

  // === COLLECTION TRANSLATIONS STATE ===
  const [collectionTranslations, setCollectionTranslations] = useState({})
  const [showCollectionTranslationsForm, setShowCollectionTranslationsForm] =
    useState(false)
  const [editingCollectionName, setEditingCollectionName] = useState("")
  const [collectionTranslationFormData, setCollectionTranslationFormData] =
    useState({
      it: "",
      en: "",
      fr: "",
      de: "",
    })
  const [availableTranslations, setAvailableTranslations] = useState({})

  // === UI STATE ===
  const [loading, setLoading] = useState(false)
  const [view, setView] = useState("types")
  const [showMaterialTypeManager, setShowMaterialTypeManager] = useState(false)
  const [editingTypeIndex, setEditingTypeIndex] = useState(null)
  const [editingTypeData, setEditingTypeData] = useState(null)

  // === INITIALIZATION ===
  useEffect(() => {
    initializeComponent()
  }, [isEditing, family, initialData])

  useEffect(() => {
    loadAvailableTranslations()
  }, [products])

  const initializeComponent = async () => {
    await loadAllMaterials()
    await loadAvailableFamilyProducts()

    if (isEditing && initialData) {
      initializeFormData()
    } else if (!isEditing) {
      setFormData((prev) => ({ ...prev, name: "" }))
    }
  }

  const initializeFormData = () => {
    const types = []
    const familyData = initialData

    setFormData((prev) => ({ ...prev, name: family }))

    // Initialize collection translations
    if (
      familyData.collectionTranslations &&
      typeof familyData.collectionTranslations === "object"
    ) {
      setCollectionTranslations({ ...familyData.collectionTranslations })
    }

    // Initialize type links
    if (familyData.typeLinks && Array.isArray(familyData.typeLinks)) {
      setTypeLinks([...familyData.typeLinks])
    }

    // Initialize material types
    if (familyData.defaultMaterials) {
      // MODIFICATO: Create array with order information
      const typesWithOrder = Object.entries(familyData.defaultMaterials).map(
        ([techName, config]) => {
          const standardMaterialCodes = createExactCodesArray(
            config.standard || []
          )
          const optionalMaterialCodes = createExactCodesArray(
            config.optional || []
          )

          const standardMaterialNames = standardMaterialCodes.map((code) => {
            const material = materialManager.findMaterialBySlug?.(code.trim())
            return material ? material.name : code
          })

          const optionalMaterialNames = optionalMaterialCodes.map((code) => {
            const material = materialManager.findMaterialBySlug?.(code.trim())
            return material ? material.name : code
          })

          return {
            technicalName: techName,
            displayLabel: config.label || techName,
            display: config.display || false,
            hidden: config.hidden || false,
            lang: config.lang || { it: "", en: "", fr: "", de: "" },
            materials: [...standardMaterialNames, ...optionalMaterialNames],
            standardMaterials: standardMaterialNames,
            optionalMaterials: optionalMaterialNames,
            _exactStandardCodes: standardMaterialCodes,
            _exactOptionalCodes: optionalMaterialCodes,
            _originalConfig: { ...config },
            materialTypeOrder:
              typeof config.materialTypeOrder === "number"
                ? config.materialTypeOrder
                : 999,
          }
        }
      )

      // AGGIUNTO: Sort by materialTypeOrder to preserve saved order
      typesWithOrder.sort((a, b) => a.materialTypeOrder - b.materialTypeOrder)

      types.push(...typesWithOrder)
    }

    setCustomTypes(types)
  }

  // === MATERIAL LOADING ===
  const loadAllMaterials = async () => {
    setLoading(true)
    try {
      const materialsData = await fetchMaterials()
      setAllMaterials(materialsData.materials || [])

      const uniqueCollections = new Set()
      materialsData.materials.forEach((material) => {
        if (material.collection) {
          uniqueCollections.add(material.collection)
        }
      })
      setCollections(Array.from(uniqueCollections).sort())

      if (materialManager.allMaterials.length === 0) {
        materialManager.initializeAllMaterials()
      }
    } catch (error) {
      console.error("Failed to load materials:", error)
    } finally {
      setLoading(false)
    }
  }

  const loadAvailableFamilyProducts = async () => {
    if (!isEditing || !category || !family || !products) return

    try {
      const familyProducts = products.filter(
        (p) => p.category === category && p.family === family
      )
      setAvailableFamilyProducts(familyProducts)

      if (
        currentSelectedProduct?.category === category &&
        currentSelectedProduct?.family === family
      ) {
        setSelectedProduct(currentSelectedProduct)
        await loadProductMaterials(currentSelectedProduct.code)
      }
    } catch (error) {
      console.error("Failed to load family products:", error)
    }
  }

  const loadAvailableTranslations = () => {
    const translationsMap = {}

    products.forEach((product) => {
      if (product._familyDefaults?.collectionTranslations) {
        const familyTranslations =
          product._familyDefaults.collectionTranslations
        Object.entries(familyTranslations).forEach(
          ([collectionName, translations]) => {
            if (!translationsMap[collectionName]) {
              translationsMap[collectionName] = {}
            }
            Object.assign(translationsMap[collectionName], translations)
          }
        )
      }
    })

    setAvailableTranslations(translationsMap)
  }

  // === PRODUCT CROSS-CHECK ===
  const loadProductMaterials = async (productCode) => {
    if (!productCode) return

    console.log("Loading materials for product:", productCode)
    setIsLoadingProduct(true)

    // Reset completo dei dati precedenti
    setMaterialComparisonData({})
    setFetchedMaterials([])
    setCrossCheckKey((prev) => prev + 1) // Forza refresh UI

    try {
      const productData = await fetchProductByCode(productCode)
      setSelectedProduct(productData)

      // Attendi che il viewer sia pronto
      const waitForViewer = async (maxAttempts = 15, delay = 800) => {
        for (let attempt = 1; attempt <= maxAttempts; attempt++) {
          try {
            if (window.emViewers?.["emersyaIframe"]) {
              const globalConfig = await window.emViewers[
                "emersyaIframe"
              ].getCurrentGlobalConfiguration()
              if (globalConfig?.sceneGraph?.localId) {
                return globalConfig
              }
            }
          } catch (error) {
            console.log(`Attempt ${attempt}: Viewer error:`, error.message)
          }

          if (attempt < maxAttempts) {
            await new Promise((resolve) => setTimeout(resolve, delay))
          }
        }
        throw new Error("Viewer did not become ready within timeout")
      }

      try {
        const globalConfig = await waitForViewer()
        const configurableMaterials = await window.emViewers[
          "emersyaIframe"
        ].getProductNodeInstanceConfigurableMaterials({
          localId: globalConfig.sceneGraph.localId,
        })

        const processedMaterials = configurableMaterials.map((mat) => ({
          technicalName: mat.name,
          variations: mat.materialVariations.map((variation) => ({
            name: variation.name.trim(),
            viewerName: variation.name,
          })),
        }))

        console.log(
          "Fetched materials for",
          productCode,
          ":",
          processedMaterials
        )
        setFetchedMaterials(processedMaterials)

        // Auto-abilita cross-check se ci sono materiali e non Ã¨ giÃ  abilitato
        if (!crossCheckEnabled && processedMaterials.length > 0) {
          setCrossCheckEnabled(true)
        }

        // Esegui comparison se cross-check Ã¨ abilitato
        if (crossCheckEnabled && customTypes.length > 0) {
          setTimeout(() => {
            performMaterialComparison(processedMaterials)
          }, 300)
        }
      } catch (viewerError) {
        console.warn("Could not fetch materials from viewer:", viewerError)
        setFetchedMaterials([])
        setMaterialComparisonData({})
      }
    } catch (error) {
      console.error("Failed to load product materials:", error)
      setFetchedMaterials([])
      setMaterialComparisonData({})
    } finally {
      setIsLoadingProduct(false)
    }
  }

  const performMaterialComparison = (fetchedMaterials) => {
    const comparisonData = {}

    // Mappa normalizzata dei tipi configurati per matching case-insensitive
    const configuredTypesMap = new Map()
    customTypes.forEach((type) => {
      const totalMaterials =
        (type.standardMaterials?.length || 0) +
        (type.optionalMaterials?.length || 0)
      if (totalMaterials >= 2) {
        configuredTypesMap.set(type.technicalName.toLowerCase().trim(), type)
      }
    })

    // 1. PRIMA FASE: Elabora TUTTI i material types dal prodotto (solo quelli con 2+ materiali)
    const validFetchedMaterials = fetchedMaterials.filter(
      (fm) => fm.variations && fm.variations.length >= 2
    )

    validFetchedMaterials.forEach((fetchedType) => {
      const fetchedTypeName = fetchedType.technicalName
      const fetchedTypeKey = fetchedTypeName.toLowerCase().trim()
      const fetchedMaterials = fetchedType.variations.map((v) => v.name.trim())

      // Cerca se abbiamo un tipo configurato che matcha
      const matchingConfiguredType = configuredTypesMap.get(fetchedTypeKey)

      if (matchingConfiguredType) {
        // TIPO CONFIGURATO E PRESENTE NEL PRODOTTO
        const configuredStandardSlugs =
          matchingConfiguredType._exactStandardCodes ||
          matchingConfiguredType.standardMaterials.map((name) => {
            const material = materialManager.findMaterialByName(name)
            return material ? material.slug : name
          })
        const configuredOptionalSlugs =
          matchingConfiguredType._exactOptionalCodes ||
          matchingConfiguredType.optionalMaterials.map((name) => {
            const material = materialManager.findMaterialByName(name)
            return material ? material.slug : name
          })

        const allConfiguredSlugs = [
          ...configuredStandardSlugs,
          ...configuredOptionalSlugs,
        ]

        const missing = fetchedMaterials.filter(
          (fetchedName) =>
            !allConfiguredSlugs.some(
              (configuredSlug) =>
                configuredSlug === fetchedName ||
                configuredSlug.trim() === fetchedName.trim()
            )
        )
        const notInProduct = allConfiguredSlugs.filter(
          (configuredSlug) =>
            !fetchedMaterials.some(
              (fetchedName) =>
                configuredSlug === fetchedName ||
                configuredSlug.trim() === fetchedName.trim()
            )
        )

        comparisonData[fetchedTypeName] = {
          displayName: fetchedTypeName, // Usa il nome dal prodotto
          configuredName: matchingConfiguredType.technicalName,
          isConfigured: true,
          fetchedMaterials: fetchedMaterials,
          configuredMaterials: allConfiguredSlugs,
          missing,
          notInProduct,
          hasIssues: missing.length > 0,
          typeExists: true,
          severity:
            missing.length > 0
              ? "error"
              : notInProduct.length > 0
              ? "warning"
              : "ok",
          details: {
            standardCount: configuredStandardSlugs.length,
            optionalCount: configuredOptionalSlugs.length,
            fetchedCount: fetchedMaterials.length,
            missingCount: missing.length,
            extraCount: notInProduct.length,
          },
        }

        // Rimuovi dalla mappa per evitare di processarlo di nuovo
        configuredTypesMap.delete(fetchedTypeKey)
      } else {
        // TIPO PRESENTE NEL PRODOTTO MA NON CONFIGURATO
        comparisonData[fetchedTypeName] = {
          displayName: fetchedTypeName,
          configuredName: null,
          isConfigured: false,
          fetchedMaterials: fetchedMaterials,
          configuredMaterials: [],
          missing: fetchedMaterials, // Tutti i materiali sono "mancanti" dalla configurazione
          notInProduct: [],
          hasIssues: true,
          typeNotConfigured: true,
          severity: "warning",
          details: {
            standardCount: 0,
            optionalCount: 0,
            fetchedCount: fetchedMaterials.length,
            missingCount: fetchedMaterials.length,
            extraCount: 0,
          },
        }
      }
    })

    // 2. SECONDA FASE: Processa i tipi configurati che NON sono nel prodotto
    configuredTypesMap.forEach((configuredType, normalizedKey) => {
      const allConfiguredSlugs = [
        ...(configuredType._exactStandardCodes ||
          configuredType.standardMaterials),
        ...(configuredType._exactOptionalCodes ||
          configuredType.optionalMaterials),
      ]

      comparisonData[configuredType.technicalName] = {
        displayName: configuredType.technicalName,
        configuredName: configuredType.technicalName,
        isConfigured: true,
        fetchedMaterials: [],
        configuredMaterials: allConfiguredSlugs,
        missing: [],
        notInProduct: allConfiguredSlugs, // Tutti i materiali configurati non sono nel prodotto
        hasIssues: false,
        typeNotFound: true,
        severity: "info",
        details: {
          standardCount: (
            configuredType._exactStandardCodes ||
            configuredType.standardMaterials
          ).length,
          optionalCount: (
            configuredType._exactOptionalCodes ||
            configuredType.optionalMaterials
          ).length,
          fetchedCount: 0,
          missingCount: 0,
          extraCount: allConfiguredSlugs.length,
        },
      }
    })

    console.log("Updated comparison data:", comparisonData)
    setMaterialComparisonData(comparisonData)
    setCrossCheckKey((prev) => prev + 1)
  }

  const handleLoadProductInViewer = async (productCode) => {
    if (!productCode || !onProductChange) return

    try {
      const productObject = availableFamilyProducts.find(
        (p) => p.code === productCode
      )
      if (!productObject) {
        alert("Prodotto non trovato")
        return
      }

      setFetchedMaterials([])
      setMaterialComparisonData({})
      setIsLoadingProduct(true)

      await onProductChange(productCode)
      await new Promise((resolve) => setTimeout(resolve, 1000))
      await loadProductMaterials(productCode)

      const displayName =
        productObject.lang?.it || productObject.name || productObject.code
      alert(`Prodotto "${displayName}" caricato nel viewer`)
    } catch (error) {
      console.error("Failed to load product in viewer:", error)
      alert(`Errore nel caricamento del prodotto: ${error.message}`)
    }
  }

  const handleToggleCrossCheck = () => {
    const newState = !crossCheckEnabled
    setCrossCheckEnabled(newState)

    if (newState) {
      if (fetchedMaterials.length > 0 && customTypes.length > 0) {
        // Force immediate update
        setTimeout(() => {
          performMaterialComparison(fetchedMaterials)
        }, 100)
      } else if (selectedProduct && customTypes.length > 0) {
        loadProductMaterials(selectedProduct.code)
      }
    } else {
      setMaterialComparisonData({})
      setCrossCheckKey((prev) => prev + 1)
    }
  }
  useEffect(() => {
    if (
      crossCheckEnabled &&
      fetchedMaterials.length > 0 &&
      customTypes.length > 0
    ) {
      const timeoutId = setTimeout(() => {
        performMaterialComparison(fetchedMaterials)
      }, 300)
      return () => clearTimeout(timeoutId)
    }
  }, [customTypes, crossCheckEnabled, fetchedMaterials])
  // === MATERIAL TYPES MANAGEMENT ===
  const handleAddType = () => {
    setEditingTypeIndex(null)
    setEditingTypeData(null)
    setShowMaterialTypeManager(true)
  }

  const handleEditType = (index) => {
    const type = customTypes[index]
    setEditingTypeIndex(index)
    setEditingTypeData({
      technicalName: type.technicalName,
      displayLabel: type.displayLabel,
      display: Boolean(type.display),
      hidden: Boolean(type.hidden),
      lang: type.lang || { it: "", en: "", fr: "", de: "" },
      materials: type.materials,
      standardMaterials: type.standardMaterials,
      optionalMaterials: type.optionalMaterials,
      _exactStandardCodes: type._exactStandardCodes,
      _exactOptionalCodes: type._exactOptionalCodes,
      _originalConfig: type._originalConfig,
    })
    setShowMaterialTypeManager(true)
  }

  const handleSaveType = (typeData) => {
    const updatedTypeData = { ...typeData }

    // Process exact codes
    const getOriginalCode = (name, existingType) => {
      if (existingType) {
        let index = existingType.standardMaterials.indexOf(name)
        if (index > -1 && existingType._exactStandardCodes?.[index]) {
          return existingType._exactStandardCodes[index]
        }
        index = existingType.optionalMaterials.indexOf(name)
        if (index > -1 && existingType._exactOptionalCodes?.[index]) {
          return existingType._exactOptionalCodes[index]
        }
      }
      if (name.match(/^[A-Z]+\d+$/)) return name
      const material = materialManager.findMaterialByName(name)
      return material?.slug || name
    }

    if (editingTypeIndex !== null) {
      const existingType = customTypes[editingTypeIndex]
      updatedTypeData._exactStandardCodes = typeData.standardMaterials.map(
        (name) => getOriginalCode(name, existingType)
      )
      updatedTypeData._exactOptionalCodes = typeData.optionalMaterials.map(
        (name) => getOriginalCode(name, existingType)
      )
      if (existingType._originalConfig) {
        updatedTypeData._originalConfig = { ...existingType._originalConfig }
      }
    } else {
      updatedTypeData._exactStandardCodes = typeData.standardMaterials.map(
        (name) => {
          if (name.match(/^[A-Z]+\d+$/)) return name
          const material = materialManager.findMaterialByName(name)
          return material?.slug || name
        }
      )
      updatedTypeData._exactOptionalCodes = typeData.optionalMaterials.map(
        (name) => {
          if (name.match(/^[A-Z]+\d+$/)) return name
          const material = materialManager.findMaterialByName(name)
          return material?.slug || name
        }
      )
    }

    // Update custom types
    const updatedTypes = [...customTypes]
    if (editingTypeIndex !== null) {
      updatedTypes[editingTypeIndex] = updatedTypeData
    } else {
      updatedTypes.push(updatedTypeData)
    }
    setCustomTypes(updatedTypes)

    // Update form data
    const updatedDefaultMaterials = { ...formData.defaultMaterials }
    if (
      editingTypeIndex !== null &&
      customTypes[editingTypeIndex].technicalName !== typeData.technicalName
    ) {
      delete updatedDefaultMaterials[
        customTypes[editingTypeIndex].technicalName
      ]
    }

    const materialConfig = {
      ...(updatedTypeData.originalConfig || {}),
      label: typeData.displayLabel,
      display: Boolean(typeData.display),
      hidden: Boolean(typeData.hidden), // <-- AGGIUNTO: salva hidden
      lang: typeData.lang,
      standard: updatedTypeData.exactStandardCodes || [],
      optional: updatedTypeData.exactOptionalCodes || [],
    }

    updatedDefaultMaterials[typeData.technicalName] = materialConfig

    setFormData((prev) => ({
      ...prev,
      defaultMaterials: updatedDefaultMaterials,
    }))

    setShowMaterialTypeManager(false)
  }

  const handleDeleteType = (index) => {
    if (!window.confirm("Are you sure you want to delete this type?")) return

    const typeToDelete = customTypes[index]
    const updatedTypes = [...customTypes]
    updatedTypes.splice(index, 1)
    setCustomTypes(updatedTypes)

    const updatedDefaultMaterials = { ...formData.defaultMaterials }
    delete updatedDefaultMaterials[typeToDelete.technicalName]

    setFormData((prev) => ({
      ...prev,
      defaultMaterials: updatedDefaultMaterials,
    }))
  }

  const handleDuplicateType = (index) => {
    const typeToDuplicate = customTypes[index]
    const newType = {
      ...typeToDuplicate,
      technicalName: `${typeToDuplicate.technicalName}_copy`,
      displayLabel: `${typeToDuplicate.displayLabel} (Copy)`,
    }

    setCustomTypes((prev) => [...prev, newType])

    const updatedDefaultMaterials = { ...formData.defaultMaterials }
    updatedDefaultMaterials[newType.technicalName] = {
      label: newType.displayLabel,
      display: Boolean(newType.display),
      hidden: Boolean(newType.hidden),
      lang: newType.lang,
      standard: newType.standardMaterials.map((name) => {
        const material = materialManager.findMaterialByName(name)
        return material ? material.slug : materialManager.generateSlug(name)
      }),
      optional: newType.optionalMaterials.map((name) => {
        const material = materialManager.findMaterialByName(name)
        return material ? material.slug : materialManager.generateSlug(name)
      }),
    }

    setFormData((prev) => ({
      ...prev,
      defaultMaterials: updatedDefaultMaterials,
    }))
  }

  // === DRAG AND DROP ===
  const handleMaterialTypesReorder = useCallback(
    (result) => {
      if (!result.destination) return

      const items = Array.from(customTypes)
      const [reorderedItem] = items.splice(result.source.index, 1)
      items.splice(result.destination.index, 0, reorderedItem)

      setCustomTypes(items)

      // Update formData maintaining order
      const reorderedDefaultMaterials = {}
      items.forEach((type) => {
        if (formData.defaultMaterials[type.technicalName]) {
          reorderedDefaultMaterials[type.technicalName] =
            formData.defaultMaterials[type.technicalName]
        }
      })

      setFormData((prev) => ({
        ...prev,
        defaultMaterials: reorderedDefaultMaterials,
      }))
    },
    [customTypes, formData.defaultMaterials]
  )

  // === COLLECTION TRANSLATIONS ===
  const handleAddCollectionTranslation = () => {
    setEditingCollectionName("")
    setCollectionTranslationFormData({ it: "", en: "", fr: "", de: "" })
    setShowCollectionTranslationsForm(true)
  }

  const handleEditCollectionTranslation = (collectionName) => {
    setEditingCollectionName(collectionName)
    const existing = collectionTranslations[collectionName] || {}
    setCollectionTranslationFormData({
      it: existing.it || "",
      en: existing.en || "",
      fr: existing.fr || "",
      de: existing.de || "",
    })
    setShowCollectionTranslationsForm(true)
  }

  const handleSaveCollectionTranslation = () => {
    if (!editingCollectionName.trim()) {
      alert("Nome collezione richiesto")
      return
    }

    const updatedTranslations = {
      ...collectionTranslations,
      [editingCollectionName]: { ...collectionTranslationFormData },
    }

    setCollectionTranslations(updatedTranslations)
    setFormData((prev) => ({
      ...prev,
      collectionTranslations: updatedTranslations,
    }))
    setShowCollectionTranslationsForm(false)
  }

  const handleDeleteCollectionTranslation = (collectionName) => {
    if (!window.confirm(`Eliminare le traduzioni per "${collectionName}"?`))
      return

    const updatedTranslations = { ...collectionTranslations }
    delete updatedTranslations[collectionName]

    setCollectionTranslations(updatedTranslations)
    setFormData((prev) => ({
      ...prev,
      collectionTranslations: updatedTranslations,
    }))
  }

  const handleCopyTranslationFromFamily = (collectionName) => {
    if (availableTranslations[collectionName]) {
      const updatedTranslations = {
        ...collectionTranslations,
        [collectionName]: { ...availableTranslations[collectionName] },
      }

      setCollectionTranslations(updatedTranslations)
      setFormData((prev) => ({
        ...prev,
        collectionTranslations: updatedTranslations,
      }))
    }
  }

  // === FORM SUBMISSION ===
  const handleSubmit = (e) => {
    e.preventDefault()

    console.log("🔍 DEBUG - customTypes before submit:", customTypes)
    console.log("🔍 DEBUG - customTypes length:", customTypes.length)
    console.log("🔍 DEBUG - typeLinks before submit:", typeLinks)
    console.log("🔍 DEBUG - formData.typeLinks:", formData.typeLinks)

    let saveData = {
      name: formData.name,
      defaultMaterials: {},
      typeLinks: typeLinks.length > 0 ? typeLinks : formData.typeLinks || [],
      collectionTranslations: collectionTranslations || {},
    }

    // Build defaultMaterials from customTypes
    if (customTypes?.length > 0) {
      console.log("🔧 Processing customTypes for save...")
      customTypes.forEach((type, typeIndex) => {
        console.log(`🔧 Processing type ${typeIndex}:`, {
          name: type.technicalName,
          order: typeIndex,
          displayLabel: type.displayLabel,
        })

        const materialConfig = {
          ...(type.originalConfig || {}),
          label: type.displayLabel,
          display: Boolean(type.display),
          hidden: Boolean(type.hidden),
          lang: type.lang || { it: "", en: "", fr: "", de: "" },
          materialTypeOrder: typeIndex, // Assegna ordine basato su posizione
        }

        // Handle materials
        if (type.materials?.length > 0) {
          const exactStandardCodes = type._exactStandardCodes || []
          const exactOptionalCodes = type._exactOptionalCodes || []

          materialConfig.standard = exactStandardCodes
          materialConfig.optional = exactOptionalCodes
        } else {
          materialConfig.standard = []
          materialConfig.optional = []
        }

        saveData.defaultMaterials[type.technicalName] = materialConfig
      })
      console.log(
        "🔧 Final saveData.defaultMaterials:",
        saveData.defaultMaterials
      )
    }

    console.log("📦 Final saveData being sent:", saveData)
    onSave(saveData)
  }

  // === UTILITY FUNCTIONS ===
  const handleChange = (e) => {
    const { name, value } = e.target
    setFormData((prev) => ({ ...prev, [name]: value }))
  }

  const createExactCodesArray = (codeArray) => {
    if (!Array.isArray(codeArray)) return []
    return codeArray.map((code) => code)
  }

  // === RENDER ===
  return (
    <div className="ay-fixed ay-inset-0 ay-bg-black ay-bg-opacity-50 ay-flex ay-items-center ay-justify-center ay-z-50">
      <div className="ay-bg-white ay-p-6 ay-rounded-lg ay-shadow-lg ay-w-full ay-max-w-6xl ay-max-h-screen ay-overflow-auto ay-flex ay-flex-col">
        <h2 className="ay-text-xl ay-font-bold ay-mb-4">
          {isEditing ? "Edit Family" : "Add Family"} in Category: {category}
        </h2>

        {/* Product Cross-Check Section */}
        {isEditing && (
          <div className="ay-mb-4 ay-p-3 ay-bg-blue-50 ay-rounded-lg">
            <div className="ay-flex ay-justify-between ay-items-center ay-mb-2">
              <div className="ay-flex ay-items-center ay-gap-4">
                <h3 className="ay-text-md ay-font-medium">
                  Product Cross-Check
                </h3>
                <label className="ay-flex ay-items-center ay-gap-1 ay-text-sm">
                  <input
                    type="checkbox"
                    checked={crossCheckEnabled}
                    onChange={handleToggleCrossCheck}
                    className="ay-rounded"
                  />
                  <span>Enable</span>
                </label>

                {availableFamilyProducts.length > 0 ? (
                  <select
                    value={selectedProduct?.code || ""}
                    onChange={(e) => {
                      const selectedCode = e.target.value
                      if (selectedCode && !isLoadingProduct) {
                        handleLoadProductInViewer(selectedCode)
                      }
                    }}
                    disabled={isLoadingProduct}
                    className="ay-px-2 ay-py-1 ay-border ay-border-gray-300 ay-rounded ay-text-sm ay-max-w-48 ay-disabled:ay-opacity-50"
                  >
                    <option value="">Select product...</option>
                    {availableFamilyProducts.map((product) => (
                      <option key={product.code} value={product.code}>
                        {product.lang?.it || product.name || product.code}
                      </option>
                    ))}
                  </select>
                ) : null}

                {isLoadingProduct && (
                  <span className="ay-text-xs ay-text-blue-600">
                    Loading materials...
                  </span>
                )}
              </div>
            </div>

            {selectedProduct && (
              <div className="ay-mb-2 ay-p-2 ay-bg-white ay-rounded ay-border">
                <div className="ay-flex ay-justify-between ay-items-center">
                  <div>
                    <p className="ay-text-sm ay-font-medium">
                      {selectedProduct.lang?.it ||
                        selectedProduct.name ||
                        selectedProduct.code}
                    </p>
                    <p className="ay-text-xs ay-text-gray-500">
                      Code: {selectedProduct.code} | Materials:{" "}
                      {fetchedMaterials.length} types
                    </p>
                  </div>
                  <div className="ay-flex ay-items-center ay-gap-2">
                    <span className="ay-text-xs ay-bg-green-100 ay-text-green-800 ay-px-2 ay-py-1 ay-rounded">
                      Loaded
                    </span>
                    <span className="ay-text-xs ay-text-gray-500">
                      {fetchedMaterials.reduce(
                        (total, type) => total + type.variations.length,
                        0
                      )}{" "}
                      materials
                    </span>
                  </div>
                </div>
              </div>
            )}

            {crossCheckEnabled &&
              Object.keys(materialComparisonData).length > 0 && (
                <div
                  key={crossCheckKey}
                  className="ay-max-h-48 ay-overflow-y-auto ay-space-y-2 ay-text-xs ay-border ay-border-gray-200 ay-rounded ay-p-2"
                >
                  <div className="ay-font-medium ay-mb-2 ay-sticky ay-top-0 ay-bg-white ay-z-10">
                    Cross-Check Results (
                    {Object.keys(materialComparisonData).length} types):
                  </div>
                  {Object.entries(materialComparisonData)
                    .sort(([a], [b]) => a.localeCompare(b)) // Ordina alfabeticamente
                    .map(([typeName, data]) => {
                      const getStatusColor = () => {
                        switch (data.severity) {
                          case "error":
                            return "ay-bg-red-100 ay-text-red-700 ay-border-red-300"
                          case "warning":
                            return "ay-bg-orange-100 ay-text-orange-700 ay-border-orange-300"
                          case "info":
                            return "ay-bg-gray-100 ay-text-gray-600 ay-border-gray-300"
                          default:
                            return "ay-bg-green-100 ay-text-green-700 ay-border-green-300"
                        }
                      }

                      const getStatusText = () => {
                        if (data.typeNotFound) return "Not in product"
                        if (data.typeNotConfigured) return "Not configured"
                        if (data.missing.length > 0)
                          return `${data.missing.length} missing`
                        if (data.notInProduct.length > 0)
                          return `${data.notInProduct.length} extra`
                        return "OK"
                      }

                      const getIcon = () => {
                        if (!data.isConfigured) return "⚙️" // Non configurato
                        if (data.typeNotFound) return "❌" // Configurato ma non nel prodotto
                        switch (data.severity) {
                          case "error":
                            return "❌"
                          case "warning":
                            return "⚠️"
                          case "info":
                            return "ℹ️"
                          default:
                            return "✅"
                        }
                      }

                      return (
                        <div
                          key={typeName}
                          className={`ay-p-2 ay-rounded ay-border ${getStatusColor()}`}
                        >
                          <div className="ay-flex ay-justify-between ay-items-center">
                            <span className="ay-font-medium ay-truncate ay-flex-grow ay-mr-2">
                              {data.displayName}
                              {data.configuredName &&
                                data.configuredName !== data.displayName && (
                                  <span className="ay-text-xs ay-opacity-75">
                                    {" "}
                                    → {data.configuredName}
                                  </span>
                                )}
                            </span>
                            <span className="ay-flex ay-items-center ay-gap-1 ay-flex-shrink-0">
                              {getIcon()} {getStatusText()}
                            </span>
                          </div>

                          <div className="ay-mt-1 ay-text-xs ay-opacity-75">
                            In Product: {data.details.fetchedCount} |
                            Configured:{" "}
                            {data.details.standardCount +
                              data.details.optionalCount}
                            {data.details.missingCount > 0 &&
                              ` | Missing: ${data.details.missingCount}`}
                            {data.details.extraCount > 0 &&
                              ` | Extra: ${data.details.extraCount}`}
                          </div>
                        </div>
                      )
                    })}
                </div>
              )}
          </div>
        )}

        <form className="ay-flex-grow ay-overflow-auto">
          <div className="ay-mb-4">
            <label className="ay-block ay-text-sm ay-font-medium ay-text-gray-700 ay-mb-1">
              Family Name
            </label>
            <input
              type="text"
              name="name"
              value={formData.name}
              onChange={handleChange}
              className="ay-block ay-w-full ay-px-3 ay-py-2 ay-border ay-border-gray-300 ay-rounded-md ay-shadow-sm ay-focus:ay-outline-none ay-focus:ay-ring-blue-500 ay-focus:ay-border-blue-500"
              required
            />
          </div>

          {/* Navigation Tabs */}
          <div className="ay-border-b ay-border-gray-200 ay-mb-4">
            <nav className="ay-flex ay-space-x-4">
              <button
                type="button"
                onClick={() => setView("types")}
                className={`ay-py-2 ay-px-3 ay-text-sm ay-font-medium ${
                  view === "types"
                    ? "ay-border-b-2 ay-border-blue-500 ay-text-blue-600"
                    : "ay-text-gray-500 ay-hover:ay-text-gray-700"
                }`}
              >
                Material Types
              </button>
              <button
                type="button"
                onClick={() => setView("typeLinks")}
                className={`ay-py-2 ay-px-3 ay-text-sm ay-font-medium ${
                  view === "typeLinks"
                    ? "ay-border-b-2 ay-border-blue-500 ay-text-blue-600"
                    : "ay-text-gray-500 ay-hover:ay-text-gray-700"
                }`}
              >
                Type Links (Vincoli)
              </button>
              <button
                type="button"
                onClick={() => setView("collectionTranslations")}
                className={`ay-py-2 ay-px-3 ay-text-sm ay-font-medium ${
                  view === "collectionTranslations"
                    ? "ay-border-b-2 ay-border-blue-500 ay-text-blue-600"
                    : "ay-text-gray-500 ay-hover:ay-text-gray-700"
                }`}
              >
                Traduzioni Collezioni
              </button>
            </nav>
          </div>

          {/* Material Types View */}
          {view === "types" && (
            <div className="ay-mb-6">
              <div className="ay-flex ay-justify-between ay-items-center ay-mb-2">
                <h3 className="ay-text-lg ay-font-medium">
                  Material Types (Configurable Material in Emersya)
                </h3>
                <button
                  type="button"
                  onClick={handleAddType}
                  className="ay-px-3 ay-py-1 ay-bg-blue-500 ay-text-white ay-rounded ay-text-sm"
                >
                  Add Type
                </button>
              </div>

              {/* Types List with Drag&Drop */}
              <div className="ay-bg-gray-50 ay-p-3 ay-rounded-md ay-mb-4">
                {customTypes.length > 0 ? (
                  <div className="ay-space-y-3">
                    {customTypes.map((type, index) => (
                      <div
                        key={type.technicalName}
                        className="ay-border ay-rounded-md ay-p-3 ay-cursor-pointer ay-transition-[border-color] ay-relative ay-bg-white ay-border-gray-300 ay-hover:ay-border-gray-400"
                      >
                        {/* Status Indicators */}
                        <div className="ay-absolute ay-top-1 ay-right-1 ay-flex ay-gap-1">
                          {type.display && (
                            <span
                              className="ay-text-xs ay-bg-green-500 ay-text-white ay-px-1 ay-rounded"
                              title="Display filtered"
                            >
                              D
                            </span>
                          )}
                          {type.hidden && (
                            <span
                              className="ay-text-xs ay-bg-red-500 ay-text-white ay-px-1 ay-rounded"
                              title="Hidden"
                            >
                              H
                            </span>
                          )}
                          {crossCheckEnabled &&
                            materialComparisonData[type.technicalName] && (
                              <span
                                className={`ay-text-xs ay-px-1 ay-rounded ${
                                  materialComparisonData[type.technicalName]
                                    .hasIssues
                                    ? "ay-bg-red-500 ay-text-white"
                                    : "ay-bg-green-500 ay-text-white"
                                }`}
                                title="Cross-check status"
                              >
                                {materialComparisonData[type.technicalName]
                                  .hasIssues
                                  ? "!"
                                  : "✓"}
                              </span>
                            )}
                        </div>

                        <div className="ay-flex ay-justify-between ay-items-start ay-pr-8">
                          <div className="ay-flex-grow">
                            <h4 className="ay-font-medium">
                              {type.displayLabel}
                            </h4>
                            <p className="ay-text-xs ay-text-gray-500">
                              {type.technicalName}
                            </p>
                            <p className="ay-text-sm ay-mt-1">
                              {type.materials.length} Materials (
                              {type.standardMaterials.length} Standard,{" "}
                              {type.optionalMaterials.length} Optional)
                            </p>
                          </div>

                          {/* Order Controls */}
                          <div className="ay-flex ay-flex-col ay-gap-1 ay-mr-4">
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation()
                                if (index > 0) {
                                  const newTypes = [...customTypes]
                                  ;[newTypes[index], newTypes[index - 1]] = [
                                    newTypes[index - 1],
                                    newTypes[index],
                                  ]
                                  setCustomTypes(newTypes)

                                  // IMPORTANTE: Aggiorna formData.defaultMaterials con il nuovo ordine
                                  const updatedDefaultMaterials = {}
                                  newTypes.forEach((type, newIndex) => {
                                    const techName = type.technicalName
                                    if (formData.defaultMaterials[techName]) {
                                      updatedDefaultMaterials[techName] = {
                                        ...formData.defaultMaterials[techName],
                                        materialTypeOrder: newIndex,
                                      }
                                    }
                                  })

                                  setFormData((prev) => ({
                                    ...prev,
                                    defaultMaterials: updatedDefaultMaterials,
                                  }))
                                }
                              }}
                              disabled={index === 0}
                              className="ay-p-1 ay-text-xs ay-bg-gray-200 ay-rounded hover:ay-bg-gray-300 disabled:ay-opacity-50 disabled:ay-cursor-not-allowed"
                              title="Move up"
                            >
                              ↑
                            </button>

                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation()
                                if (index < customTypes.length - 1) {
                                  const newTypes = [...customTypes]
                                  ;[newTypes[index], newTypes[index + 1]] = [
                                    newTypes[index + 1],
                                    newTypes[index],
                                  ]
                                  setCustomTypes(newTypes)

                                  // IMPORTANTE: Aggiorna formData.defaultMaterials con il nuovo ordine
                                  const updatedDefaultMaterials = {}
                                  newTypes.forEach((type, newIndex) => {
                                    const techName = type.technicalName
                                    if (formData.defaultMaterials[techName]) {
                                      updatedDefaultMaterials[techName] = {
                                        ...formData.defaultMaterials[techName],
                                        materialTypeOrder: newIndex,
                                      }
                                    }
                                  })

                                  setFormData((prev) => ({
                                    ...prev,
                                    defaultMaterials: updatedDefaultMaterials,
                                  }))
                                }
                              }}
                              disabled={index === customTypes.length - 1}
                              className="ay-p-1 ay-text-xs ay-bg-gray-200 ay-rounded hover:ay-bg-gray-300 disabled:ay-opacity-50 disabled:ay-cursor-not-allowed"
                              title="Move down"
                            >
                              ↓
                            </button>
                          </div>
                        </div>

                        <div className="ay-flex ay-justify-end ay-gap-1 ay-mt-2">
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation()
                              handleEditType(index)
                            }}
                            className="ay-text-xs ay-text-blue-600 ay-hover:ay-text-blue-800"
                          >
                            Edit
                          </button>
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation()
                              handleDuplicateType(index)
                            }}
                            className="ay-text-xs ay-text-green-600 ay-hover:ay-text-green-800"
                          >
                            Duplicate
                          </button>
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation()
                              handleDeleteType(index)
                            }}
                            className="ay-text-xs ay-text-red-600 ay-hover:ay-text-red-800"
                          >
                            Delete
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="ay-text-center ay-py-4 ay-text-gray-500">
                    No material types defined. Add a type to get started.
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Type Links View */}
          {view === "typeLinks" && (
            <div>
              <h3 className="ay-text-lg ay-font-medium ay-mb-4">Type Links</h3>
              <div className="ay-mb-4 ay-bg-blue-50 ay-p-4 ay-rounded">
                <p className="ay-text-sm ay-text-gray-700 ay-mb-2">
                  I Type Links permettono di collegare diversi material types
                  per sincronizzare automaticamente le selezioni.
                </p>
              </div>
              <TypeLinksComponent
                customTypes={customTypes}
                initialLinks={typeLinks}
                onChange={(updatedLinks) => {
                  console.log("🔗 TypeLinks changed:", updatedLinks)
                  setTypeLinks(updatedLinks)
                  setFormData((prev) => ({
                    ...prev,
                    typeLinks: updatedLinks,
                  }))
                }}
                setLoading={setLoading}
                allMaterials={allMaterials}
              />
            </div>
          )}

          {/* Collection Translations View */}
          {view === "collectionTranslations" && (
            <div>
              <div className="ay-flex ay-justify-between ay-items-center ay-mb-4">
                <h3 className="ay-text-lg ay-font-medium">
                  Traduzioni Collezioni
                </h3>
                <button
                  type="button"
                  onClick={handleAddCollectionTranslation}
                  className="ay-px-3 ay-py-1 ay-bg-blue-500 ay-text-white ay-rounded ay-text-sm"
                >
                  Aggiungi Traduzione
                </button>
              </div>

              <div className="ay-mb-4 ay-bg-blue-50 ay-p-4 ay-rounded">
                <h4 className="ay-font-medium ay-mb-2">
                  Traduzioni Collezioni Materiali
                </h4>
                <p className="ay-text-sm">
                  Qui puoi definire le traduzioni per i nomi delle collezioni di
                  materiali che verranno visualizzate nel configuratore per
                  questa famiglia specifica.
                </p>
              </div>

              {/* Available Translations to Copy */}
              {Object.keys(availableTranslations).length > 0 && (
                <div className="ay-mb-6 ay-bg-green-50 ay-border ay-border-green-200 ay-rounded-lg ay-p-4">
                  <h4 className="ay-font-medium ay-text-green-800 ay-mb-3">
                    ðŸ“‹ Traduzioni Disponibili da Altre Famiglie
                  </h4>
                  <div className="ay-space-y-2">
                    {Object.entries(availableTranslations)
                      .filter(
                        ([collectionName]) =>
                          !collectionTranslations[collectionName]
                      )
                      .map(([collectionName, translations]) => (
                        <div
                          key={collectionName}
                          className="ay-flex ay-justify-between ay-items-center ay-bg-white ay-p-2 ay-rounded ay-border"
                        >
                          <div>
                            <span className="ay-font-medium">
                              {collectionName}
                            </span>
                            <div className="ay-text-xs ay-text-gray-600 ay-mt-1">
                              IT: {translations.it} | EN: {translations.en} |
                              FR: {translations.fr} | DE: {translations.de}
                            </div>
                          </div>
                          <button
                            type="button"
                            onClick={() =>
                              handleCopyTranslationFromFamily(collectionName)
                            }
                            className="ay-px-2 ay-py-1 ay-bg-green-500 ay-text-white ay-rounded ay-text-xs ay-hover:ay-bg-green-600"
                          >
                            Copia
                          </button>
                        </div>
                      ))}
                  </div>
                </div>
              )}

              {/* Configured Translations */}
              {Object.keys(collectionTranslations).length > 0 ? (
                <div className="ay-space-y-3">
                  {Object.entries(collectionTranslations).map(
                    ([collectionName, translations]) => (
                      <div
                        key={collectionName}
                        className="ay-border ay-rounded-md ay-p-4"
                      >
                        <div className="ay-flex ay-justify-between ay-items-start ay-mb-3">
                          <h4 className="ay-font-medium">{collectionName}</h4>
                          <div className="ay-flex ay-gap-2">
                            <button
                              type="button"
                              onClick={() =>
                                handleEditCollectionTranslation(collectionName)
                              }
                              className="ay-text-xs ay-text-blue-600 ay-hover:ay-text-blue-800"
                            >
                              Modifica
                            </button>
                            <button
                              type="button"
                              onClick={() =>
                                handleDeleteCollectionTranslation(
                                  collectionName
                                )
                              }
                              className="ay-text-xs ay-text-red-600 ay-hover:ay-text-red-800"
                            >
                              Elimina
                            </button>
                          </div>
                        </div>
                        <div className="ay-grid ay-grid-cols-2 md:ay-grid-cols-4 ay-gap-3 ay-text-sm">
                          <div>
                            <span className="ay-font-medium">IT:</span>{" "}
                            {translations.it || "-"}
                          </div>
                          <div>
                            <span className="ay-font-medium">EN:</span>{" "}
                            {translations.en || "-"}
                          </div>
                          <div>
                            <span className="ay-font-medium">FR:</span>{" "}
                            {translations.fr || "-"}
                          </div>
                          <div>
                            <span className="ay-font-medium">DE:</span>{" "}
                            {translations.de || "-"}
                          </div>
                        </div>
                      </div>
                    )
                  )}
                </div>
              ) : (
                <div className="ay-text-center ay-py-8 ay-text-gray-500">
                  <p>Nessuna traduzione definita.</p>
                  <button
                    type="button"
                    onClick={handleAddCollectionTranslation}
                    className="ay-mt-2 ay-text-blue-500 ay-underline"
                  >
                    Aggiungi la prima traduzione
                  </button>
                </div>
              )}
            </div>
          )}
          <div className="ay-flex ay-gap-3">
            <button
              type="button"
              onClick={onCancel}
              className="ay-px-3 ay-py-1 ay-border ay-border-gray-300 ay-rounded ay-shadow-sm ay-bg-white ay-text-gray-700 ay-hover:ay-bg-gray-50 ay-transition-colors ay-text-sm"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleSubmit}
              className="ay-px-3 ay-py-1 ay-border ay-border-transparent ay-rounded ay-shadow-sm ay-bg-blue-500 ay-text-white ay-hover:ay-bg-blue-600 ay-transition-colors ay-text-sm"
            >
              {isEditing ? "Update Family" : "Create Family"}
            </button>
          </div>
        </form>

        {/* Material Type Manager Modal */}
        {showMaterialTypeManager && (
          <MaterialTypeManager
            isEditing={editingTypeIndex !== null}
            initialData={editingTypeData}
            allMaterials={allMaterials}
            collections={collections}
            crossCheckEnabled={crossCheckEnabled}
            fetchedMaterials={fetchedMaterials}
            onSave={handleSaveType}
            onCancel={() => setShowMaterialTypeManager(false)}
          />
        )}

        {/* Collection Translation Modal */}
        {showCollectionTranslationsForm && (
          <div className="ay-fixed ay-inset-0 ay-bg-black ay-bg-opacity-50 ay-flex ay-items-center ay-justify-center ay-z-50">
            <div className="ay-bg-white ay-p-6 ay-rounded-lg ay-shadow-lg ay-w-full ay-max-w-md">
              <h3 className="ay-text-lg ay-font-bold ay-mb-4">
                {editingCollectionName ? "Modifica" : "Aggiungi"} Traduzione
                Collezione
              </h3>

              <div className="ay-mb-4">
                <label className="ay-block ay-text-sm ay-font-medium ay-text-gray-700 ay-mb-1">
                  Nome Collezione *
                </label>
                <input
                  type="text"
                  value={editingCollectionName}
                  onChange={(e) => setEditingCollectionName(e.target.value)}
                  className="ay-block ay-w-full ay-px-3 ay-py-2 ay-border ay-border-gray-300 ay-rounded-md"
                  placeholder="es. Tessuti per termoformato"
                  required
                />
              </div>

              <div className="ay-space-y-3 ay-mb-6">
                {["it", "en", "fr", "de"].map((lang) => (
                  <div key={lang}>
                    <label className="ay-block ay-text-sm ay-font-medium ay-text-gray-700 ay-mb-1">
                      {lang.toUpperCase()}
                    </label>
                    <input
                      type="text"
                      value={collectionTranslationFormData[lang]}
                      onChange={(e) =>
                        setCollectionTranslationFormData((prev) => ({
                          ...prev,
                          [lang]: e.target.value,
                        }))
                      }
                      className="ay-block ay-w-full ay-px-3 ay-py-2 ay-border ay-border-gray-300 ay-rounded-md"
                      placeholder={`Traduzione in ${lang.toUpperCase()}`}
                    />
                  </div>
                ))}
              </div>

              <div className="ay-flex ay-justify-end ay-gap-3">
                <button
                  type="button"
                  onClick={() => setShowCollectionTranslationsForm(false)}
                  className="ay-px-4 ay-py-2 ay-border ay-border-gray-300 ay-rounded-md ay-text-gray-700"
                >
                  Annulla
                </button>
                <button
                  type="button"
                  onClick={handleSaveCollectionTranslation}
                  className="ay-px-4 ay-py-2 ay-bg-blue-500 ay-text-white ay-rounded-md"
                >
                  Salva
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

export default FamilyForm
